import { AttentionBoxVariant, AvAttentionBox, AvTooltip } from '@fabric-ui/components';
import { Link as LinkIcon } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { Box, Button } from '@mui/material';
import { Link } from '@utils/AvRouter';
import { useAvContext } from '../../context/AvContextProvider';
import { APP_PATHS, PAGE_PATHS } from '../../types';
import { IProjectionToAffectedFields, SettingsName, SettingType } from './hooks';

export const AffectedFieldsTooltip = ({ projectionToAffectedFields }: { projectionToAffectedFields: IProjectionToAffectedFields[] }) => {
  const {
    accountEntities: { fieldMap, getFieldIdFromProjAndName },
  } = useAvContext();

  if (!projectionToAffectedFields?.length) {
    return null;
  }

  return (
    <AvTooltip
      color="white"
      title={projectionToAffectedFields.map(item => (
        <div key={item.projectionId.name}>
          {item.fields.map(field => (
            <Box key={field}>{fieldMap[getFieldIdFromProjAndName(item.projectionId.name, field)]?.title || field}</Box>
          ))}
        </div>
      ))}
      sx={{ ...flex.col, gap: 1 }}>
      <Box sx={{ textDecoration: 'underline', color: 'var(--color-content-interactive-primary-default)' }}>Affected Fields</Box>
    </AvTooltip>
  );
};

function SettingNotSyncedBox({
  onClick,
  settingsType,
  projectionToAffectedFields = [],
  alongWith,
}: {
  onClick?: () => void;
  settingsType: SettingType;
  projectionToAffectedFields?: IProjectionToAffectedFields[];
  alongWith?: string[];
}) {
  const { getPathName } = useAvContext();

  const settingsToPagePathParams = {
    [SettingType.GroupingRules]: [`${APP_PATHS.SETTINGS}/${PAGE_PATHS.RULE_SET}`, undefined, APP_PATHS.VULNERABILITIES],
    [SettingType.Score]: [`${APP_PATHS.SETTINGS}/${PAGE_PATHS.SCORE}`, undefined, APP_PATHS.VULNERABILITIES],
    [SettingType.SeveritySLA]: [`${APP_PATHS.SETTINGS}/${PAGE_PATHS.SEVERITY_SLA}`, undefined, APP_PATHS.VULNERABILITIES],
    [SettingType.ASSET_AGING]: [PAGE_PATHS.ASSET_AGING],
    [SettingType.Automations]: [
      `${APP_PATHS.SETTINGS}/${PAGE_PATHS.TICKET_LIFECYCLE}`,
      `/${PAGE_PATHS.AUTOMATIONS}`,
      APP_PATHS.VULNERABILITIES,
    ],
    [SettingType.LOCKED_AUTOMATION]: [
      `${APP_PATHS.SETTINGS}/${PAGE_PATHS.TICKET_LIFECYCLE}`,
      `/${PAGE_PATHS.AUTOMATIONS}`,
      APP_PATHS.VULNERABILITIES,
    ],
    [SettingType.STATUS_AUTOMATION]: [
      `${APP_PATHS.SETTINGS}/${PAGE_PATHS.TICKET_LIFECYCLE}`,
      `/${PAGE_PATHS.AUTOMATIONS}`,
      APP_PATHS.VULNERABILITIES,
    ],
    [SettingType.SEVERITY_AUTOMATION]: [
      `${APP_PATHS.SETTINGS}/${PAGE_PATHS.SEVERITY_SLA}`,
      `/${PAGE_PATHS.SEVERITY}`,
      APP_PATHS.VULNERABILITIES,
    ],
    [SettingType.SLA_AUTOMATION]: [`${APP_PATHS.SETTINGS}/${PAGE_PATHS.SEVERITY_SLA}`, `/${PAGE_PATHS.SLA}`, APP_PATHS.VULNERABILITIES],
    [SettingType.UnificationRules]: [
      `${PAGE_PATHS.DATA_UNIFICATION_ENTITIES}/${PAGE_PATHS.DATA_UNIFICATION_FIELDS}`,
      undefined,
      APP_PATHS.PLATFORM,
    ],
    [SettingType.STATUS_AUTOMATION_AEM]: [
      `${APP_PATHS.SETTINGS}/${PAGE_PATHS.TICKET_LIFECYCLE}`,
      `/${PAGE_PATHS.AUTOMATIONS}`,
      APP_PATHS.ASSETS,
    ],
    [SettingType.LOCKED_AUTOMATION_AEM]: [
      `${APP_PATHS.SETTINGS}/${PAGE_PATHS.TICKET_LIFECYCLE}`,
      `/${PAGE_PATHS.AUTOMATIONS}`,
      APP_PATHS.ASSETS,
    ],
    [SettingType.GroupingRulesAem]: [`${APP_PATHS.SETTINGS}/${PAGE_PATHS.RULE_SET}`, undefined, APP_PATHS.ASSETS],
  };
  return (
    <AvAttentionBox
      content={
        <Box
          sx={{
            ...flex.justifyBetweenCenter,
            fontSize: 14,
            fontWeight: 400,
            gap: 1,
            a: { color: 'var(--color-content-interactive-primary-default)', fontWeight: 600 },
          }}>
          <Box
            sx={{
              ...flex.itemsCenter,
              gap: 1,
            }}>
            Configured through{' '}
            <Link
              style={{ ...flex.itemsCenter, gap: 1 }}
              to={
                getPathName(
                  ...(settingsToPagePathParams[settingsType]
                    ? settingsToPagePathParams[settingsType]
                    : [PAGE_PATHS.MODEL_MANAGEMENT, undefined, APP_PATHS.PLATFORM])
                ).to
              }>
              {settingsType ? SettingsName[settingsType] : SettingsName[SettingType.DATA_MODEL]}
              <LinkIcon />
            </Link>
            {alongWith?.length ? (
              <>
                along with{' '}
                <Box component="span" sx={{ fontWeight: '600' }}>
                  {alongWith.join(', ')}
                </Box>
              </>
            ) : null}
            {!!onClick && (
              <Button variant="outlined" size="xSmall" onClick={onClick}>
                Unlink & Override
              </Button>
            )}
          </Box>
          <AffectedFieldsTooltip projectionToAffectedFields={projectionToAffectedFields} />
        </Box>
      }
      variant={AttentionBoxVariant.Warning}
    />
  );
}

export default SettingNotSyncedBox;
